<template>
  <div>
    <LoadingVehicle v-if="saving" />
    <QForm v-else @submit="saveColourAndExtras">
      <h6 class="q-mt-none q-mb-lg text-grey-8">Colour And Extras</h6>
      <div
        class="row justify-center"
        v-if="$apollo.queries.vehicleOptions.loading"
      >
        <QSpinner size="3em" color="grey-7" />
      </div>
      <div v-else>
        <FormError :show="$v.$error && $v.$dirty" class="q-my-md" />

        <div class="row q-col-gutter-md">
          <div class="col-6">
            <p class="text-weight-bold text-grey-9">Exterior Colours*</p>
          </div>
          <div class="col-6">
            <p class="text-weight-bold text-grey-9">
              Interior Colours*
            </p>
            <InformationCaption
              v-if="allRequired[interiorColour]"
              class="q-mb-sm"
              caption="This has been automatically selected based on the exterior colour"
            />
          </div>
        </div>

        <div class="row q-col-gutter-md">
          <ColourAndExtrasExteriorColour
            class="col-6"
            :disabled="isDisabled"
            :colours="vehicleOptions.exteriorColours"
            :error="$v.exteriorColour.$error"
            :precluded="allPrecluded"
            v-model="$v.exteriorColour.$model"
          />
          <ColourAndExtrasInteriorColour
            class="col-6"
            :colours="vehicleOptions.interiorColours"
            :error="$v.interiorColour.$error"
            :precluded="allPrecluded"
            :disabled="
              !selectedExteriorColour.id ||
              allRequired[interiorColour] ||
              isDisabled
            "
            v-model="$v.interiorColour.$model"
          />
        </div>

        <QSeparator class="q-my-md" v-if="false" />

        <div class="row" v-if="false">
          <div class="col">
            <p class="text-weight-bold text-grey-9">Extras</p>
            <InformationCaption
              v-if="requiresOptionalExtra"
              class="q-mb-sm"
              caption="Some of the extras below have been automatically added based on
            your selections above"
            />
            <ColourAndExtrasOptionalEquipment
              :equipment="vehicleOptions.optionalEquipment"
              :required="allRequired"
              :precluded="allPrecluded"
              :disabled="
                !selectedExteriorColour.id ||
                !selectedInteriorColour.id ||
                isDisabled
              "
              v-model="$v.optionalEquipment.$model"
            />
          </div>
        </div>

        <QSeparator class="q-my-md" />
        <div class="row">
          <div class="col">
            <p class="q-mb-md text-weight-bold text-grey-9">
              Additional Accessories
            </p>
            <ColourAndExtrasAdditionalAccessories
              :disabled="isDisabled"
              v-model="adhocEquipment"
            />
          </div>
        </div>

        <QSeparator class="q-my-md" />

        <div class="row q-col-gutter-md">
          <div class="col-6">
            <p class="text-body3 text-grey-7 q-mb-xs text-weight-medium">
              Drive away cost (inc gst)
            </p>
            <QInput
              v-model="$v.onRoadCost.$model"
              input-class="hide-input-arrows"
              filled
              color="secondary"
              dense
              clearable
              type="number"
              step=".01"
              prefix="$"
              :disable="noOnRoadCost || isDisabled"
              :readonly="noOnRoadCost"
              :error="$v.onRoadCost.$error"
              :rules="[
                () =>
                  $v.onRoadCost.isValidPrice ||
                  'Luxury Vehicle – contact Channel for quote'
              ]"
            />
          </div>
        </div>
      </div>
      <QuoteNavigationActions back-to="quoteLeaseDetails">
        <template slot="next" v-if="!$apollo.queries.vehicleOptions.loading">
          <QBtn
            type="submit"
            :label="isDisabled ? 'Next' : 'Save and Next'"
            :icon-right="this.nextIcon"
            color="secondary"
            :loading="saving"
            :disable="saving"
            :ripple="false"
          />
        </template>
      </QuoteNavigationActions>
    </QForm>
  </div>
</template>

<script>
import {QSeparator, QForm, QBtn, QInput, Icons, QSpinner} from 'eclipx.ui';
import {mapState, mapActions, mapGetters} from 'vuex';
import {required, requiredIf, decimal} from 'vuelidate/lib/validators';
import QuoteNavigationActions from '../QuoteNavigationActions';
import FormError from '../../../components/FormError/FormError';
import ColourAndExtrasExteriorColour from './ColourAndExtrasExteriorColour';
import ColourAndExtrasInteriorColour from './ColourAndExtrasInteriorColour';
import ColourAndExtrasOptionalEquipment from './ColourAndExtrasOptionalEquipment';
import ColourAndExtrasAdditionalAccessories from './ColourAndExtrasAdditionalAccessories';
import QueryVehicleOptions from '../../../graphql/queries/QueryVehicleOptions';
import InformationCaption from '../../../components/InformationCaption/InformationCaption';
import {GLOBAL_ERROR} from '../../../components/GlobalError/GlobalError';
import LoadingVehicle from '../../../components/LoadingVehicle/LoadingVehicle';

const OPTION_TYPES = {
  E: 'exteriorColours',
  I: 'interiorColours',
  O: 'optionalEquipment'
};

const DATA_TYPES = {
  E: 'exteriorColour',
  I: 'interiorColour',
  O: 'optionalEquipment'
};

export default {
  name: 'QuoteColoursAndExtras',
  data() {
    return {
      saving: false,
      // Default value pre apollo query result
      vehicleOptions: {
        exteriorColours: [],
        interiorColours: [],
        optionalEquipment: [],
        includedEquipment: []
      },
      exteriorColour: null,
      interiorColour: null,
      optionalEquipment: [],
      adhocEquipment: [],
      onRoadCost: null,
      noOnRoadCost: false
    };
  },
  apollo: {
    vehicleOptions: {
      query: QueryVehicleOptions,
      variables: function () {
        return {
          variantId: this.erpquote.variant.id.toString()
        };
      },
      skip() {
        return !this.$store.getters['quote/quoteLeaseValid'];
      },
      error: function () {
        this.$euiNotifications.emit({
          notification: GLOBAL_ERROR,
          action: () => {
            return this.$apollo.queries.vehicleOptions.refresh();
          }
        });
      }
    }
  },
  validations: {
    exteriorColour: {required},
    interiorColour: {required},
    optionalEquipment: {},
    onRoadCost: {
      required: requiredIf((component) => !component.noOnRoadCost),
      decimal,
      isValidFormat(value) {
        const regex = /^[0-9]{3,9}(\.[0-9]{1,2})?$/;
        if (this.noOnRoadCost) {
          return true;
        }
        return regex.test(value);
      },
      isValidPrice(value) {
        try {
          const price = parseFloat(value);
          const maxPrice = process.env.VUE_APP_MAX_VEHICLE_PRICE || 70000;
          if (price > maxPrice)
            // if price is over this means luxury vehicle
            return false;
        } catch {}
        return true;
      }
    }
  },
  computed: {
    ...mapState({erpquote: (state) => state.quote.quote.erpquote}),
    ...mapGetters({
      hasRequestedPricing: 'quote/hasRequestedPricing',
      isExpired: 'quote/isExpired',
      saveQuoteFields: 'quote/saveQuoteFields'
    }),
    isDisabled: function () {
      return this.hasRequestedPricing || this.isExpired;
    },
    selectedExteriorColour: function () {
      return this.findVehicleOption(this.exteriorColour);
    },
    selectedInteriorColour: function () {
      return this.findVehicleOption(this.interiorColour);
    },
    selectedOptionalEquipment: function () {
      return this.optionalEquipment.map((selectedOption) =>
        this.findVehicleOption(selectedOption)
      );
    },
    allRequired: function () {
      return {
        ...this.getConditionValues(this.selectedExteriorColour.requires),
        ...this.getConditionValues(this.selectedInteriorColour.requires)
      };
    },
    allPrecluded: function () {
      return {
        ...this.getConditionValues(this.selectedExteriorColour.precludes),
        ...this.getConditionValues(this.selectedInteriorColour.precludes),
        ...this.optionalEquipment.reduce((accumulator, currentValue) => {
          return {
            ...accumulator,
            ...this.getConditionValues(currentValue.precludes)
          };
        }, {})
      };
    },
    requiresOptionalExtra: function () {
      return Boolean(
        Object.keys(this.allRequired).find(
          (option) => option.charAt(0).toLocaleLowerCase() === 'o'
        )
      );
    }
  },
  watch: {
    noOnRoadCost: function () {
      this.onRoadCost = null;
    },
    // If the quote has updated and removed colours and extras from the summary card, update all page input values
    quote: function () {
      this.bindQuoteToInputs();
      this.$v.$touch();
    },
    allRequired: function (values, oldValues) {
      // Remove colours / options that were required but are now not required
      Object.keys(oldValues).forEach((key) => {
        if (!values[key]) {
          this.removeSelection(key);
        }
      });
      // Ensure all required colours / options are added
      Object.keys(values).forEach((key) => this.addSelection(key));
    },
    allPrecluded: function (values) {
      Object.keys(values).forEach((key) => this.removeSelection(key));
    }
  },
  methods: {
    // Returns a vehicle option with an associated ID
    findVehicleOption(id) {
      if (!id) {
        return {};
      }
      let optionType = OPTION_TYPES[id.charAt(0).toUpperCase()];
      let searchArray = this.vehicleOptions[optionType];
      return searchArray.find((option) => option.id === id) || {};
    },
    getConditionValues(obj = {}) {
      let conditions = {};
      for (let key in obj) {
        // Ignore or conditions
        if (key === 'OR') {
          continue;
        }
        // Add all within an AND (excluding nested or conditions)
        if (key === 'AND') {
          conditions = obj[key].reduce((accumulator, currentValue) => {
            if (typeof currentValue === 'string') {
              accumulator[currentValue] = true;
            }
            return accumulator;
          }, conditions);
        }
      }
      return conditions;
    },
    addSelection(id) {
      let type = id.charAt(0).toUpperCase();
      switch (OPTION_TYPES[type]) {
        case OPTION_TYPES.E:
        case OPTION_TYPES.I:
          if (this[DATA_TYPES[type]] !== id) {
            this[DATA_TYPES[type]] = id;
          }
          break;
        case OPTION_TYPES.O:
          if (!this[DATA_TYPES[type]].find((option) => option === id)) {
            this[DATA_TYPES[type]].unshift(id);
          }
          break;
      }
    },
    removeSelection(id) {
      let type = id.charAt(0).toUpperCase();
      switch (OPTION_TYPES[type]) {
        case OPTION_TYPES.E:
        case OPTION_TYPES.I:
          if (this[DATA_TYPES[type]] === id) {
            this[DATA_TYPES[type]] = null;
          }
          break;
        case OPTION_TYPES.O:
          let index = this[DATA_TYPES[type]].findIndex(
            (option) => option === id
          );
          if (index > -1) {
            this[DATA_TYPES[type]].splice(index, 1);
          }
          break;
      }
    },
    bindQuoteToInputs() {
      this.exteriorColour = this.erpquote.exteriorColour.id;
      this.interiorColour = this.erpquote.interiorColour.id;
      this.optionalEquipment = [
        ...this.erpquote.optionalEquipment.map((option) => option.id)
      ];
      this.adhocEquipment = this.erpquote.adhocEquipment;
      this.onRoadCost = this.erpquote.price.onRoadCost;
    },
    goToNext() {
      this.$router.push({
        name: 'quoteOrderDetails'
        // params: { quoteId: this.$route.params.quoteId }
      });
    },
    saveColourAndExtras: async function () {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      if (!this.isDisabled) {
        let onRoadCost = this.onRoadCost ? parseFloat(this.onRoadCost) : null;

        let updatedValues = {
          exteriorColour: this.selectedExteriorColour,
          interiorColour: this.selectedInteriorColour,
          optionalEquipment: this.selectedOptionalEquipment,
          adhocEquipment: this.adhocEquipment,
          price: {
            ...this.erpquote.price,
            onRoadCost
          }
        };

        this.saveQuoteColourAndExtras({
          ...this.erpquote,
          ...updatedValues
        });
        this.goToNext();
      } else {
        this.goToNext();
      }
    },
    ...mapActions({saveQuoteColourAndExtras: 'quote/saveColourAndExtras'})
  },
  created() {
    // If the prior page is invalid, go to the first incomplete page
    if (!this.$store.getters['quote/quoteLeaseValid']) {
      this.$router.replace({
        name: this.$store.getters['quote/firstIncompletePage'],
        params: {quoteId: this.$route.params.quoteId}
      });
    }

    this.bindQuoteToInputs();

    this.nextIcon = Icons.evaArrowForward;
    this.backIcon = Icons.evaArrowBack;
  },
  components: {
    QSeparator,
    QForm,
    QBtn,
    QInput,
    QuoteNavigationActions,
    FormError,
    QSpinner,
    ColourAndExtrasExteriorColour,
    ColourAndExtrasInteriorColour,
    ColourAndExtrasOptionalEquipment,
    ColourAndExtrasAdditionalAccessories,
    InformationCaption,
    LoadingVehicle
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="sass" scoped>
.center-button
  margin-top: 2px
</style>
